import { useState } from 'react';

import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import {
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { CompactTablePagination, EmptyState, ErrorState, LoadingState, TablePageShell } from '@/shared/ui';

import { usePriceLifecycleAudit } from '../../hooks';
import { formatDateTime, formatPrice } from '../../lib/format';

/** Read-only price-lifecycle audit log. */
export function PriceAuditPanel() {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const query = usePriceLifecycleAudit({ page, size });
  const entries = query.data?.content ?? [];

  if (query.isLoading) return <LoadingState label="Загрузка журнала…" />;
  if (query.isError) {
    return (
      <ErrorState
        title="Не удалось загрузить журнал"
        message={query.error?.message ?? 'Произошла ошибка.'}
        onRetry={() => void query.refetch()}
      />
    );
  }
  if (entries.length === 0) {
    return (
      <EmptyState
        title="Журнал пуст"
        description="Изменений цен пока не зафиксировано."
        icon={<HistoryRoundedIcon />}
      />
    );
  }

  return (
    <TablePageShell
      footer={
        <CompactTablePagination
          count={query.data?.totalElements ?? 0}
          page={page}
          rowsPerPage={size}
          hideRowsPerPage
          onPageChange={(_e, next) => setPage(next)}
          onRowsPerPageChange={(e) => {
            setSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
        />
      }
    >
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Дата</TableCell>
              <TableCell>Товар</TableCell>
              <TableCell>Переход</TableCell>
              <TableCell>Действие</TableCell>
              <TableCell>Изменение</TableCell>
              <TableCell>Автор</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry) => (
              <TableRow key={entry.id} hover>
                <TableCell>{formatDateTime(entry.createdAt)}</TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {entry.productId}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {entry.fromState ? (
                      <Chip size="small" variant="outlined" label={entry.fromState} />
                    ) : null}
                    <span>→</span>
                    <Chip size="small" color="primary" variant="outlined" label={entry.toState} />
                  </Stack>
                </TableCell>
                <TableCell>{entry.action}</TableCell>
                <TableCell>
                  {entry.oldValue != null || entry.newValue != null
                    ? `${formatPrice(entry.oldValue)} → ${formatPrice(entry.newValue)}`
                    : '—'}
                </TableCell>
                <TableCell>{entry.actor ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </TablePageShell>
  );
}
